import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/cn'
import { X } from 'lucide-react'
import Button from './Button'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  mobileSize?: 'bottom-sheet' | 'fullscreen' | 'default'
  showCloseButton?: boolean
  closeOnOverlayClick?: boolean
  closeOnEsc?: boolean
  className?: string
  overlayClassName?: string
  headerClassName?: string
  contentClassName?: string
  footer?: React.ReactNode
  hideFooter?: boolean
  loading?: boolean
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  mobileSize = 'bottom-sheet',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  className,
  overlayClassName,
  headerClassName,
  contentClassName,
  footer,
  hideFooter = false,
  loading = false,
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!closeOnEsc || !isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [closeOnEsc, isOpen, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  // Size classes for desktop
  const desktopSizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  }

  // Mobile size classes
  const mobileSizeClasses = {
    'bottom-sheet': cn(
      'fixed bottom-0 left-0 right-0 rounded-t-2xl rounded-b-none',
      'max-h-[90vh] animate-slide-in-up'
    ),
    'fullscreen': cn(
      'fixed inset-0 m-0 rounded-none',
      'max-h-[100dvh] animate-fade-in'
    ),
    'default': cn(
      'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      'mx-4 max-w-full',
      'animate-scale-in'
    ),
  }

  const isMobile = window.innerWidth < 768

  const modalContent = (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'p-4 md:p-6 safe-top safe-bottom',
        overlayClassName
      )}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      {/* Overlay */}
      <div className="modal-backdrop" />

      {/* Modal */}
      <div
        className={cn(
          'z-50 w-full relative',
          'bg-surface-light dark:bg-surface-dark',
          'rounded-xl shadow-modal',
          'border border-secondary-200 dark:border-secondary-700',
          'flex flex-col max-h-[calc(100dvh-4rem)]',
          isMobile ? mobileSizeClasses[mobileSize] : desktopSizeClasses[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || description || showCloseButton) && (
          <div
            className={cn(
              'flex items-start justify-between p-6 pb-4',
              'border-b border-secondary-200 dark:border-secondary-700',
              headerClassName
            )}
          >
            <div className="flex-1 min-w-0">
              {title && (
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                  {description}
                </p>
              )}
            </div>

            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="ml-4 flex-shrink-0"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'flex-1 overflow-auto p-6',
            !title && !description && 'pt-6',
            loading && 'relative pointer-events-none',
            contentClassName
          )}
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer */}
        {!hideFooter && (
          <div
            className={cn(
              'flex items-center justify-end gap-3 p-6 pt-4',
              'border-t border-secondary-200 dark:border-secondary-700',
              !footer && 'justify-between'
            )}
          >
            {footer || (
              <>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary">Confirm</Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

// Confirmation Modal - Pre-styled for confirmations
interface ConfirmationModalProps extends Omit<ModalProps, 'children' | 'size'> {
  message: string
  confirmText?: string
  cancelText?: string
  onConfirm: () => void | Promise<void>
  confirmVariant?: 'primary' | 'danger' | 'success'
  isLoading?: boolean
  destructive?: boolean
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  confirmVariant = 'primary',
  isLoading = false,
  destructive = false,
  ...props
}) => {
  const handleConfirm = async () => {
    await onConfirm()
    props.onClose()
  }

  return (
    <Modal
      size="sm"
      mobileSize="default"
      {...props}
      footer={
        <div className="flex items-center gap-3 w-full">
          <Button
            variant="ghost"
            onClick={props.onClose}
            className="flex-1"
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant={destructive ? 'danger' : confirmVariant}
            onClick={handleConfirm}
            loading={isLoading}
            className="flex-1"
          >
            {confirmText}
          </Button>
        </div>
      }
    >
      <div className="text-center py-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-warning-600 dark:text-warning-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.346 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
          {props.title || 'Are you sure?'}
        </h3>
        <p className="text-secondary-600 dark:text-secondary-400">{message}</p>
      </div>
    </Modal>
  )
}

// Fullscreen Modal for mobile
export const FullscreenModal: React.FC<Omit<ModalProps, 'size' | 'mobileSize'>> = (props) => {
  return <Modal size="full" mobileSize="fullscreen" {...props} />
}

// Bottom Sheet for mobile actions
export const BottomSheet: React.FC<Omit<ModalProps, 'size' | 'mobileSize'>> = (props) => {
  return <Modal size="full" mobileSize="bottom-sheet" {...props} />
}

export default Modal
